import { JSX } from 'react';
import { COLORS, fillColors, getFontSize, labelColors } from '@connect-x-app/shared-ui';

export const badgePillTypes = [
  'primary',
  'neutral',
  'accent',
  'success',
  'attention',
  'caution',
  'warning',
  'disabled',
  'accent-informative',
  'success-informative',
  'attention-informative',
  'caution-informative',
  'warning-informative',
  'disabled-informative',
  'primary-outline',
  'neutral-outline',
  'accent-outline',
  'success-outline',
  'attention-outline',
  'caution-outline',
  'warning-outline',
  'disabled-outline',
  'primary-white',
  'neutral-white',
  'accent-white',
  'success-white',
  'attention-white',
  'caution-white',
  'warning-white',
  'disabled-white',
] as const;

export const badgePillSizes = ['xxs', 'xs', 's', 'm', 'l'] as const;

export type BadgePillProps = {
  leftIcon?: JSX.Element;
  onClick?: () => void;
  rightIcon?: JSX.Element;
  size: (typeof badgePillSizes)[number];
  testId?: string;
  text?: string;
  truncate?: boolean;
  type: (typeof badgePillTypes)[number];
};

export const BADGE_PILL_SIZES = {
  l: {
    contentHeight: 32,
    fontSize: getFontSize('MD'),
    minHeight: 48,
    paddingHorizontal: 16,
  },
  m: {
    contentHeight: 24,
    fontSize: getFontSize('MD'),
    minHeight: 40,
    paddingHorizontal: 16,
  },
  s: {
    contentHeight: 24,
    fontSize: getFontSize('SM'),
    minHeight: 32,
    paddingHorizontal: 8,
  },
  xs: {
    contentHeight: 18,
    fontSize: getFontSize('XS'),
    minHeight: 24,
    paddingHorizontal: 6,
  },
  xxs: {
    contentHeight: 12,
    fontSize: getFontSize('XS'),
    minHeight: 16,
    paddingHorizontal: 4,
  },
};

export const BADGE_PILL_COLOR_COMBINATIONS = {
  accent: {
    border: 'transparent',
    fill: fillColors['$fill-accent-light'],
    text: labelColors['$label-dark'],
  },
  'accent-informative': {
    border: COLORS.romeo.$09,
    fill: COLORS.romeo.$03,
    text: labelColors['$label-dark'],
  },
  'accent-outline': {
    border: COLORS.romeo.$08,
    fill: 'transparent',
    text: labelColors['$label-accent-dark'],
  },
  'accent-white': {
    border: COLORS.romeo.$08,
    fill: fillColors['$fill-white'],
    text: labelColors['$label-accent-dark'],
  },
  attention: {
    border: 'transparent',
    fill: fillColors['$fill-attention'],
    text: labelColors['$label-dark'],
  },
  'attention-informative': {
    border: COLORS.andes.$07,
    fill: COLORS.andes.$03,
    text: labelColors['$label-dark'],
  },
  'attention-outline': {
    border: COLORS.andes.$08,
    fill: 'transparent',
    text: COLORS.andes.$08,
  },
  'attention-white': {
    border: COLORS.andes.$08,
    fill: fillColors['$fill-white'],
    text: labelColors['$label-attention'],
  },
  caution: {
    border: 'transparent',
    fill: fillColors['$fill-caution-dark'],
    text: labelColors['$label-dark'],
  },
  'caution-informative': {
    border: COLORS.fuji.$08,
    fill: COLORS.fuji.$03,
    text: labelColors['$label-dark'],
  },
  'caution-outline': {
    border: COLORS.fuji.$08,
    fill: 'transparent',
    text: COLORS.fuji.$08,
  },
  'caution-white': {
    border: COLORS.fuji.$08,
    fill: fillColors['$fill-white'],
    text: COLORS.fuji.$08,
  },
  disabled: {
    border: 'transparent',
    fill: fillColors['$fill-disabled-light'],
    text: labelColors['$label-light'],
  },
  'disabled-informative': {
    border: COLORS.zulu.$02,
    fill: COLORS.tango.$01,
    text: labelColors['$label-disabled'],
  },
  'disabled-outline': {
    border: COLORS.zulu.$02,
    fill: 'transparent',
    text: labelColors['$label-disabled'],
  },
  'disabled-white': {
    border: COLORS.zulu.$02,
    fill: COLORS.tango.$01,
    text: labelColors['$label-disabled'],
  },
  neutral: {
    border: 'transparent',
    fill: fillColors['$fill-neutral'],
    text: labelColors['$label-light'],
  },
  'neutral-outline': {
    border: COLORS.zulu.$06,
    fill: 'transparent',
    text: labelColors['$label-neutral'],
  },
  'neutral-white': {
    border: COLORS.zulu.$06,
    fill: fillColors['$fill-white'],
    text: labelColors['$label-neutral'],
  },
  primary: {
    border: 'transparent',
    fill: COLORS.zulu.$08,
    text: labelColors['$label-light'],
  },
  'primary-outline': {
    border: COLORS.zulu.$10,
    fill: 'transparent',
    text: labelColors['$label-primary'],
  },
  'primary-white': {
    border: COLORS.zulu.$08,
    fill: fillColors['$fill-white'],
    text: labelColors['$label-primary'],
  },
  success: {
    border: 'transparent',
    fill: fillColors['$fill-success-dark'],
    text: labelColors['$label-dark'],
  },
  'success-informative': {
    border: COLORS.everest.$08,
    fill: COLORS.everest.$01,
    text: labelColors['$label-dark'],
  },
  'success-outline': {
    border: COLORS.everest.$08,
    fill: 'transparent',
    text: labelColors['$label-success-dark'],
  },
  'success-white': {
    border: COLORS.everest.$08,
    fill: fillColors['$fill-white'],
    text: labelColors['$label-success-dark'],
  },
  warning: {
    border: 'transparent',
    fill: fillColors['$fill-warning-dark'],
    text: labelColors['$label-light'],
  },
  'warning-informative': {
    border: COLORS.etna.$08,
    fill: COLORS.etna.$03,
    text: labelColors['$label-warning-dark'],
  },
  'warning-outline': {
    border: COLORS.etna.$06,
    fill: 'transparent',
    text: labelColors['$label-warning-light'],
  },
  'warning-white': {
    border: COLORS.etna.$06,
    fill: fillColors['$fill-white'],
    text: labelColors['$label-warning-light'],
  },
};
